import {
  APP_INITIALIZER,
  ApplicationConfig,
  EnvironmentInjector,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withNavigationErrorHandler } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { map } from 'rxjs';
import { DASHBOARD_ACCESS, DASHBOARD_CATALOG, DASHBOARD_TIMEZONE, WIDGET_SCOPE } from '@ams/dashboard';
import { STOREFRONT_ACCESS, STOREFRONT_TIMEZONE, STOREFRONT_CODE_VALUES, STOREFRONT_TENANT } from '@ams/storefront';
import { DashboardCatalogService } from './services/dashboard-catalog.service';
import { CodeValueService } from './services/code-value.service';
import { CodeCategory } from './models/code-value';
import { appRoutes } from './app.routes';
import { provideQuillConfig } from 'ngx-quill/config';
import { recoverFromStaleChunk, isStaleChunkError, showStaleChunkFallback } from '@ams/ui-kit';
import { HTTP_INTERCEPTOR_CHAIN } from './interceptors';
import { ErrorHandlerService } from './errors/error-handler.service';
import { provideFontAwesome } from './providers/font-awesome.provider';
import { provideChartJs } from './providers/chart.provider';
import { initializeThemeProvider } from './providers/theme-init.provider';
import { AccessControlService } from './services/access-control.service';
import { AppModule } from './models/access-control.model';
import { TenantSettingsService } from './services/tenant-settings.service';
import { NavigationOriginService } from './services/navigation-origin.service';
import { TenantOriginService } from './services/tenant-origin.service';
import { normalizeTenantDateFormat, normalizeTimeZone } from './utils/timezone-date.util';


export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      // A lazy route whose chunk was removed by a deploy fails here. Recover onto
      // the current build rather than surfacing a module-loading error the user
      // can do nothing with.
      withNavigationErrorHandler(({ error, url }) => {
        // `url` is the route the user was trying to reach. Recovering TO it rather
        // than reloading in place keeps the click from being swallowed — Angular
        // defers the address-bar update until a navigation succeeds.
        if (recoverFromStaleChunk(error, url)) return;
        // Recovery exhausted. This fires for the INITIAL navigation too, where no
        // component exists yet to render a message through.
        if (isStaleChunkError(error)) showStaleChunkFallback();
      }),
    ),
    provideChartJs(),
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    provideFontAwesome(),
    provideHttpClient(
      // Order is load-bearing and defined with the chain itself — see
      // HTTP_INTERCEPTOR_CHAIN, which its spec asserts against.
      withInterceptors(HTTP_INTERCEPTOR_CHAIN),
    ),
    {
      // Dashboard editing requires edit/manage on the Dashboard module; view-only
      // users get the shared lib in read-only mode. Checked at BOTH scopes so a
      // chapter admin (chapter.dashboard, in chapter context) can edit their own
      // dashboard — not just tenant admins (tenant.dashboard).
      provide: DASHBOARD_ACCESS,
      useFactory: (acl: AccessControlService) => ({
        canManage: () => {
          const canEditAt = (module: AppModule) => {
            const level = acl.getAccessLevel(module);
            return level === 'edit' || level === 'manage';
          };
          return canEditAt('tenant.dashboard') || canEditAt('chapter.dashboard');
        },
        // Admin manage pages (moved into the Customizations module). The drawer
        // links to these; the member portal omits them so the links hide there.
        templatesRoute: '/dashboard-admin/templates',
        widgetDefinitionsRoute: '/dashboard-admin/widget-definitions',
        // Member-portal-only widgets (e.g. My Event Registrations) aren't offered here.
        widgetScopes: [WIDGET_SCOPE.TENANT, WIDGET_SCOPE.CHAPTER],
      }),
      deps: [AccessControlService],
    },
    {
      // Semantic catalog for the no-SQL widget data-source builder. Membora's
      // adapter hits /api/dashboards/catalog; the library stays backend-agnostic.
      provide: DASHBOARD_CATALOG,
      useExisting: DashboardCatalogService,
    },
    {
      provide: DASHBOARD_TIMEZONE,
      useFactory: (tenantSettings: TenantSettingsService) => ({
        getTimeZone: () => normalizeTimeZone(tenantSettings.getGeneralSettings().timezone),
        getDateFormat: () => normalizeTenantDateFormat(tenantSettings.getGeneralSettings().dateFormat),
      }),
      deps: [TenantSettingsService],
    },
    {
      // @ams/storefront (admin): manage when the user can edit the storefront
      // module. STOREFRONT_API uses its library default until a screen ticket
      // provides an app-specific adapter.
      provide: STOREFRONT_ACCESS,
      useFactory: (acl: AccessControlService) => ({
        canManage: () => {
          const level = acl.getAccessLevel('tenant.storefront' as AppModule);
          return level === 'edit' || level === 'manage';
        },
        hasLevel: (required: 'edit' | 'manage') => {
          const level = acl.getAccessLevel('tenant.storefront' as AppModule);
          return required === 'manage' ? level === 'manage' : level === 'edit' || level === 'manage';
        },
      }),
      deps: [AccessControlService],
    },
    {
      provide: STOREFRONT_TIMEZONE,
      useFactory: (tenantSettings: TenantSettingsService) => ({
        getTimeZone: () => normalizeTimeZone(tenantSettings.getGeneralSettings().timezone),
        getDateFormat: () => normalizeTenantDateFormat(tenantSettings.getGeneralSettings().dateFormat),
      }),
      deps: [TenantSettingsService],
    },
    {
      // @ams/storefront: association name for admin-screen subtitles (e.g.
      // Store settings' "{orgName} · storefront configuration.") plus the
      // "View storefront" link's target — the member portal's tenant origin
      // is a different host, resolved server-side via TenantOriginService.
      provide: STOREFRONT_TENANT,
      useFactory: (tenantSettings: TenantSettingsService, tenantOrigin: TenantOriginService) => ({
        getOrgName: () => tenantSettings.orgName(),
        getStorefrontUrl: async () => {
          let origins = await tenantOrigin.getOrigins();
          // A failed lookup is memoized for the lifetime of the page, and the bounce
          // at login is usually what populated it — so drop a null and take one fresh
          // attempt rather than inherit a failure from minutes ago.
          if (!origins) {
            tenantOrigin.invalidateOrigins();
            origins = await tenantOrigin.getOrigins();
          }
          // Guarded on bounceEnabled like every other tenant-origin consumer, and here
          // there is deliberately no shared-host fallback: the public store resolves its
          // tenant from the HOST it is served on, so the shared root resolves to no
          // tenant and would bounce the admin to the member portal's login screen.
          // Where the environment can't serve tenant origins there is no usable address.
          if (!origins?.bounceEnabled || !origins.memberOrigin) return null;
          return `${origins.memberOrigin.replace(/\/+$/, '')}/store`;
        },
      }),
      deps: [TenantSettingsService, TenantOriginService],
    },
    {
      // @ams/storefront: tenant-configurable code_value labels/colors — order
      // statuses (STORE_PAYMENT_STATUS, STORE_SHIPPING_STATUS, CARRIER), the
      // product list's Type/Status filters, shipping-rate zones
      // (STORE_SHIPPING_ZONE), and coupon discount-type / applies-to
      // (STORE_COUPON_TYPE, STORE_COUPON_RESTRICT_TYPE) — one adapter over
      // CodeValueService, reusing its cache and carrying metadata so consumers
      // can derive badge variants.
      provide: STOREFRONT_CODE_VALUES,
      useFactory: (codeValues: CodeValueService) => ({
        getOptionsForCategory: (category: string) =>
          codeValues.getOptionsForCategory(category as CodeCategory).pipe(
            map((opts) => opts.map((o) => ({ code: o.code, name: o.name, metadata: o.metadata }))),
          ),
      }),
      deps: [CodeValueService],
    },
    // Theme initialization - connects tenant branding to ThemeService
    {
      provide: APP_INITIALIZER,
      useFactory: initializeThemeProvider,
      deps: [EnvironmentInjector],
      multi: true,
    },
    // Instantiate the navigation-origin tracker before the first route resolves,
    // so it sees every NavigationEnd (a lazily-injected instance would miss the
    // dashboard → page hop that "Back to Dashboard" depends on).
    {
      provide: APP_INITIALIZER,
      useFactory: (origin: NavigationOriginService) => () => void origin,
      deps: [NavigationOriginService],
      multi: true,
    },
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ header: [1, 2, 3, false] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean'],
        ],
      },
    }),
  ],
};
